import ExcelJS from "exceljs";
import type { ItemRow } from "./NewPurchaseRequestV2";
import { Currency, PurchaseRequestItemType } from "../../types";
import { MEASUREMENT_UNIT_LIST } from "../../utils";

/**
 * Single source of truth for the Purchase Request spreadsheet import.
 *
 * The same `IMPORT_COLUMNS` array drives BOTH:
 *   - generating the downloadable example template (`buildTemplateBuffer`), and
 *   - parsing an uploaded file into form rows (`parseWorkbookBuffer`).
 *
 * Because both directions read the same definitions, the template and the
 * parser can never drift apart.
 */

export type ImportWarningKind =
  | "missing-material"
  | "unknown-unit"
  | "unknown-currency"
  | "invalid-number"
  | "invalid-date";

export interface ImportWarning {
  /** 1-based spreadsheet row number the warning refers to. */
  row: number;
  /** Canonical column header the warning refers to. */
  column: string;
  kind: ImportWarningKind;
  /** The offending raw value, when useful for the message. */
  value?: string;
}

export interface ParseResult {
  ok: boolean;
  items: ItemRow[];
  warnings: ImportWarning[];
  /** Set when `ok` is false. One of: "EMPTY" | "MISSING_HEADERS". */
  error?: "EMPTY" | "MISSING_HEADERS";
}

export interface ParseOptions {
  /** Accepted measurement-unit names. Defaults to MEASUREMENT_UNIT_LIST. */
  validUnits?: string[];
  /** Accepted currency codes. Defaults to the Currency enum. */
  validCurrencies?: string[];
  /** Sheet name to read. Defaults to the first sheet. */
  sheetName?: string;
}

interface ResolvedContext {
  unitByLower: Map<string, string>;
  currencyByLower: Map<string, string>;
}

interface ColumnDef {
  /** Canonical header written to the generated template. */
  header: string;
  /** Lowercased header aliases accepted on import (header included). */
  aliases: string[];
  /** Header must be present for the file to be considered valid. */
  required?: boolean;
  /** Example value used in the generated template's sample row. */
  example: string | number;
  assign: (
    row: ItemRow,
    raw: ExcelJS.CellValue,
    rowNumber: number,
    ctx: ResolvedContext,
    warnings: ImportWarning[]
  ) => void;
}

// ---------------------------------------------------------------------------
// Cell coercion helpers
// ---------------------------------------------------------------------------

const cellToString = (raw: ExcelJS.CellValue): string => {
  if (raw == null) return "";
  if (raw instanceof Date) return raw.toISOString();
  if (typeof raw === "object") {
    const any = raw as unknown as Record<string, unknown>;
    if (typeof any.text === "string") return (any.text as string).trim();
    if (Array.isArray(any.richText)) {
      return (any.richText as { text?: string }[])
        .map((part) => part.text ?? "")
        .join("")
        .trim();
    }
    if (any.result != null) return String(any.result).trim();
    if (typeof any.hyperlink === "string")
      return (any.hyperlink as string).trim();
    return "";
  }
  return String(raw).trim();
};

const parseNumber = (raw: ExcelJS.CellValue): number | null => {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  if (typeof raw === "object") {
    const result = (raw as unknown as Record<string, unknown>).result;
    if (typeof result === "number") return result;
  }
  let s = cellToString(raw).replace(/[^0-9.,-]/g, "");
  if (!s) return null;

  const hasComma = s.includes(",");
  const hasDot = s.includes(".");
  if (hasComma && hasDot) {
    // The right-most separator is the decimal separator.
    if (s.lastIndexOf(",") > s.lastIndexOf(".")) {
      s = s.replace(/\./g, "").replace(",", ".");
    } else {
      s = s.replace(/,/g, "");
    }
  } else if (hasComma) {
    const parts = s.split(",");
    const last = parts[parts.length - 1];
    if (parts.length === 2 && last.length > 0 && last.length <= 2) {
      s = `${parts[0]}.${last}`; // decimal comma, e.g. "1234,56"
    } else {
      s = s.replace(/,/g, ""); // thousands separators
    }
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const pad2 = (n: number) => String(n).padStart(2, "0");

const formatISODate = (d: Date): string =>
  `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;

const excelSerialToDate = (serial: number): Date =>
  // Excel serial date = days since 1899-12-30 (kept in UTC to avoid tz drift).
  new Date(Date.UTC(1899, 11, 30) + Math.round(serial * 86400 * 1000));

const parseDate = (
  raw: ExcelJS.CellValue
): { value: string | null; invalid: boolean } => {
  if (raw == null || raw === "") return { value: null, invalid: false };
  if (raw instanceof Date) return { value: formatISODate(raw), invalid: false };
  if (typeof raw === "number") {
    if (raw > 0 && raw < 100000)
      return { value: formatISODate(excelSerialToDate(raw)), invalid: false };
    return { value: null, invalid: true };
  }
  const s = cellToString(raw);
  if (!s) return { value: null, invalid: false };

  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) {
    const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    if (!isNaN(d.getTime())) return { value: formatISODate(d), invalid: false };
  }
  m = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})/);
  if (m) {
    const yr = +m[3] < 100 ? +m[3] + 2000 : +m[3];
    const d = new Date(Date.UTC(yr, +m[2] - 1, +m[1]));
    if (!isNaN(d.getTime())) return { value: formatISODate(d), invalid: false };
  }
  const parsed = Date.parse(s);
  if (!isNaN(parsed))
    return { value: formatISODate(new Date(parsed)), invalid: false };
  return { value: null, invalid: true };
};

// ---------------------------------------------------------------------------
// Column schema (single source of truth)
// ---------------------------------------------------------------------------

const assignText =
  (field: "brand" | "purpose" | "address" | "costCenter" | "description") =>
  (row: ItemRow, raw: ExcelJS.CellValue) => {
    const v = cellToString(raw);
    if (v) row[field] = v;
  };

export const IMPORT_COLUMNS: ColumnDef[] = [
  {
    header: "Material",
    aliases: ["material", "producto", "produto", "product"],
    required: true,
    example: "Steel bolt M8",
    assign: (row, raw) => {
      const v = cellToString(raw);
      if (v) {
        row.materialName = v;
        row.title = v;
      }
    },
  },
  {
    header: "Internal code",
    aliases: [
      "internal code",
      "código interno",
      "codigo interno",
      "ean",
      "sku",
    ],
    example: "SKU-12345",
    assign: (row, raw) => {
      const v = cellToString(raw);
      if (v) row.materialEan = v;
    },
  },
  {
    header: "Quantity",
    aliases: ["quantity", "cantidad", "quantidade", "qty"],
    required: true,
    example: 100,
    assign: (row, raw, rowNumber, _ctx, warnings) => {
      const n = parseNumber(raw);
      if (n == null && cellToString(raw) !== "") {
        warnings.push({
          row: rowNumber,
          column: "Quantity",
          kind: "invalid-number",
          value: cellToString(raw),
        });
      }
      row.quantity = n;
    },
  },
  {
    header: "Unit",
    aliases: [
      "unit",
      "unidad",
      "unidade",
      "um",
      "presentación",
      "presentacion",
    ],
    example: "UN",
    assign: (row, raw, rowNumber, ctx, warnings) => {
      const v = cellToString(raw);
      if (!v) return;
      const match = ctx.unitByLower.get(v.toLowerCase());
      if (match) {
        row.measurementUnit = match;
      } else {
        warnings.push({
          row: rowNumber,
          column: "Unit",
          kind: "unknown-unit",
          value: v,
        });
      }
    },
  },
  {
    header: "Budget",
    aliases: [
      "budget",
      "presupuesto",
      "orçamento",
      "orcamento",
      "price",
      "precio",
      "preço",
    ],
    example: 1500,
    assign: (row, raw) => {
      const n = parseNumber(raw);
      if (n != null) row.budgetValue = n;
    },
  },
  {
    header: "Currency",
    aliases: ["currency", "moneda", "moeda"],
    example: "USD",
    assign: (row, raw, rowNumber, ctx, warnings) => {
      const v = cellToString(raw);
      if (!v) return;
      const match = ctx.currencyByLower.get(v.toLowerCase());
      if (match) {
        row.currency = match;
        row.budgetCurrency = match;
      } else {
        warnings.push({
          row: rowNumber,
          column: "Currency",
          kind: "unknown-currency",
          value: v,
        });
      }
    },
  },
  {
    header: "Needed from",
    aliases: ["needed from", "desde", "fecha desde", "data de", "from"],
    example: "2026-07-01",
    assign: (row, raw, rowNumber, _ctx, warnings) => {
      const { value, invalid } = parseDate(raw);
      if (invalid) {
        warnings.push({
          row: rowNumber,
          column: "Needed from",
          kind: "invalid-date",
          value: cellToString(raw),
        });
      }
      row.neededFrom = value;
    },
  },
  {
    header: "Needed to",
    aliases: [
      "needed to",
      "hasta",
      "fecha hasta",
      "data até",
      "data ate",
      "to",
    ],
    example: "2026-07-15",
    assign: (row, raw, rowNumber, _ctx, warnings) => {
      const { value, invalid } = parseDate(raw);
      if (invalid) {
        warnings.push({
          row: rowNumber,
          column: "Needed to",
          kind: "invalid-date",
          value: cellToString(raw),
        });
      }
      row.neededTo = value;
    },
  },
  {
    header: "Brand",
    aliases: ["brand", "marca"],
    example: "Acme",
    assign: assignText("brand"),
  },
  {
    header: "Purpose",
    aliases: ["purpose", "propósito", "proposito", "finalidad"],
    example: "Maintenance",
    assign: assignText("purpose"),
  },
  {
    header: "Address",
    aliases: ["address", "dirección", "direccion", "endereço", "endereco"],
    example: "",
    assign: assignText("address"),
  },
  {
    header: "Cost center",
    aliases: [
      "cost center",
      "centro de costo",
      "centro de custo",
      "costcenter",
    ],
    example: "CC-001",
    assign: assignText("costCenter"),
  },
  {
    header: "Description",
    aliases: [
      "description",
      "descripción",
      "descripcion",
      "descrição",
      "descricao",
      "details",
    ],
    example: "Optional notes",
    assign: assignText("description"),
  },
];

const columnByAlias = (() => {
  const map = new Map<string, ColumnDef>();
  for (const def of IMPORT_COLUMNS) {
    for (const alias of [def.header.toLowerCase(), ...def.aliases]) {
      map.set(alias, def);
    }
  }
  return map;
})();

// ---------------------------------------------------------------------------
// Row factory
// ---------------------------------------------------------------------------

const newImportedRow = (): ItemRow => ({
  title: "",
  description: "",
  quantity: null,
  measurementUnit: "",
  budgetValue: null,
  currency: Currency.USD,
  budgetCurrency: Currency.USD,
  itemType: PurchaseRequestItemType.GOOD,
  costCenter: "",
  neededFrom: null,
  neededTo: null,
  aiValidated: false,
  source: "IMPORT",
  clientId: `imp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
});

const resolveContext = (opts?: ParseOptions): ResolvedContext => {
  const units = opts?.validUnits ?? MEASUREMENT_UNIT_LIST.map((u) => u.name);
  const currencies = opts?.validCurrencies ?? Object.values(Currency);
  const unitByLower = new Map<string, string>();
  for (const u of units)
    if (u) unitByLower.set(String(u).toLowerCase(), String(u));
  const currencyByLower = new Map<string, string>();
  for (const c of currencies)
    if (c) currencyByLower.set(String(c).toLowerCase(), String(c));
  return { unitByLower, currencyByLower };
};

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

export const parseWorkbook = (
  workbook: ExcelJS.Workbook,
  opts?: ParseOptions
): ParseResult => {
  const ctx = resolveContext(opts);
  const ws = opts?.sheetName
    ? (workbook.getWorksheet(opts.sheetName) ?? workbook.worksheets[0])
    : workbook.worksheets[0];

  if (!ws || ws.rowCount === 0) {
    return { ok: false, items: [], warnings: [], error: "EMPTY" };
  }

  // Locate the header row (first row that contains any recognized header).
  let headerRowNumber = -1;
  let colMap: { col: number; def: ColumnDef }[] = [];
  ws.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (headerRowNumber !== -1) return;
    const found: { col: number; def: ColumnDef }[] = [];
    row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
      const text = cellToString(cell.value).toLowerCase();
      const def = text ? columnByAlias.get(text) : undefined;
      if (def && !found.some((f) => f.def === def)) {
        found.push({ col: colNumber, def });
      }
    });
    if (found.length) {
      headerRowNumber = rowNumber;
      colMap = found;
    }
  });

  if (headerRowNumber === -1) {
    return { ok: false, items: [], warnings: [], error: "MISSING_HEADERS" };
  }

  const mappedDefs = new Set(colMap.map((c) => c.def));
  const missingRequired = IMPORT_COLUMNS.some(
    (def) => def.required && !mappedDefs.has(def)
  );
  if (missingRequired) {
    return { ok: false, items: [], warnings: [], error: "MISSING_HEADERS" };
  }

  const items: ItemRow[] = [];
  const warnings: ImportWarning[] = [];

  ws.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber <= headerRowNumber) return;

    const rawByDef = new Map<ColumnDef, ExcelJS.CellValue>();
    let anyValue = false;
    for (const { col, def } of colMap) {
      const value = row.getCell(col).value;
      rawByDef.set(def, value);
      if (cellToString(value) !== "") anyValue = true;
    }
    if (!anyValue) return; // skip blank rows

    const item = newImportedRow();
    for (const { def } of colMap) {
      def.assign(item, rawByDef.get(def) ?? null, rowNumber, ctx, warnings);
    }
    if (!item.materialName) {
      warnings.push({
        row: rowNumber,
        column: "Material",
        kind: "missing-material",
      });
    }
    items.push(item);
  });

  return { ok: true, items, warnings };
};

const toArrayBuffer = (
  input: ArrayBuffer | Uint8Array
): ArrayBuffer | Buffer => {
  if (input instanceof Uint8Array) return Buffer.from(input);
  return input;
};

export const parseWorkbookBuffer = async (
  buffer: ArrayBuffer | Uint8Array,
  opts?: ParseOptions
): Promise<ParseResult> => {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(toArrayBuffer(buffer) as ArrayBuffer);
  return parseWorkbook(workbook, opts);
};

export const parseFile = async (
  file: File,
  opts?: ParseOptions
): Promise<ParseResult> => {
  const buffer = await file.arrayBuffer();
  return parseWorkbookBuffer(buffer, opts);
};

// ---------------------------------------------------------------------------
// Template generation
// ---------------------------------------------------------------------------

export const buildTemplateWorkbook = (): ExcelJS.Workbook => {
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet("Purchase Request");
  ws.addRow(IMPORT_COLUMNS.map((c) => c.header));
  ws.addRow(IMPORT_COLUMNS.map((c) => c.example));
  ws.getRow(1).font = { bold: true };
  IMPORT_COLUMNS.forEach((c, i) => {
    ws.getColumn(i + 1).width = Math.max(14, c.header.length + 2);
  });
  return workbook;
};

export const buildTemplateBuffer = async (): Promise<ArrayBuffer> =>
  (await buildTemplateWorkbook().xlsx.writeBuffer()) as ArrayBuffer;

export const generateTemplateBlob = async (): Promise<Blob> =>
  new Blob([await buildTemplateBuffer()], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

// ---------------------------------------------------------------------------
// Summary
// ---------------------------------------------------------------------------

export interface ImportSummary {
  rows: number;
  missingMaterial: number;
  unknownUnit: number;
  unknownCurrency: number;
  invalid: number;
}

export const summarizeResult = (result: ParseResult): ImportSummary => {
  const summary: ImportSummary = {
    rows: result.items.length,
    missingMaterial: 0,
    unknownUnit: 0,
    unknownCurrency: 0,
    invalid: 0,
  };
  for (const w of result.warnings) {
    if (w.kind === "missing-material") summary.missingMaterial += 1;
    else if (w.kind === "unknown-unit") summary.unknownUnit += 1;
    else if (w.kind === "unknown-currency") summary.unknownCurrency += 1;
    else summary.invalid += 1;
  }
  return summary;
};
